import { c as _c } from "react/compiler-runtime";
// Third Party
import * as React from 'react';
import classNames from 'classnames';

// Utils
import { routes } from '@routes';
import type { SavvySession } from '@auth';
import { extractFirstPhraseBeforeComma } from '@utils/extract-first-phrase-before-comma';
import dayjs from '@utils/dayjs-setup';
import { guestDescription } from '@utils/guest-description';

// UI
import { Icon, IconNames } from '@components/icon';
import { Link } from '@components/link';
import { useSearch } from '@contexts/search-context';
import { NavSearchForm } from '@components/search-nav/nav-search-form';
import { Typography } from '@components/typography';
import { ButtonIcon } from '@components/button-icon';
import { AnalyticsRef } from '@components/analytics-ref';
import { UserMenu } from '@components/user-menu';
type Props = {
  session: SavvySession | null;
};
const DesktopSearchNavSkeleton: React.FC = () => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="w-1/2 lg:w-3/12"><img src="/assets/svg/logos/savvy_wordmark_slate.svg" className="block h-10 w-auto" alt="Savvy" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="border-r-2 border-gray-light px-4"><div className="h-4 w-20 bg-gray-200 rounded" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="border-r-2 border-gray-light px-4"><div className="h-4 w-24 bg-gray-200 rounded" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="px-4"><div className="h-4 w-16 bg-gray-200 rounded" /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="hidden lg:flex justify-center w-6/12 h-[48px] relative"><div className="flex flex-row w-full space-x-2 items-center justify-center animate-pulse"><div className="flex flex-row rounded-full h-[48px] py-1.5 px-[7px] border border-gray-light items-center space-x-0">{t1}{t2}{t3}<div className="pl-2"><div className="h-[34px] w-[34px] bg-gray-200 rounded-full" /></div></div><div className="h-9 w-9 bg-gray-200 rounded-full" /></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <nav aria-label="Main navigation" className="hidden lg:block sticky top-0 z-50 bg-white border-b border-gray-light shadow-sm"><div className="py-4 px-4 sm:px-8"><div className="flex justify-between items-center">{t0}{t4}<div className="flex items-center justify-end w-1/2 lg:w-3/12"><div className="animate-pulse flex items-center space-x-4"><div className="h-4 w-32 bg-gray-200 rounded hidden md:block" /><div className="h-8 w-8 bg-gray-200 rounded-full" /></div></div></div></div></nav>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
export const DesktopSearchNav: React.FC<Props> = t0 => {
  const $ = _c(47);
  const {
    session
  } = t0;
  const {
    placeName,
    checkInDate,
    checkOutDate,
    adultCount,
    childCount,
    infantCount,
    petCount,
    searchNavFormActive,
    activateSearchNavForm,
    activateFeaturesModal,
    mapViewportManuallyChanged,
    listingsLoading,
    listingsQueryResults
  } = useSearch();
  const [hasInitialized, setHasInitialized] = React.useState(false);
  let t1;
  let t2;
  if ($[0] !== listingsLoading || $[1] !== listingsQueryResults) {
    t1 = () => {
      if (!listingsLoading && listingsQueryResults) {
        setHasInitialized(true);
      }
    };
    t2 = [listingsLoading, listingsQueryResults];
    $[0] = listingsLoading;
    $[1] = listingsQueryResults;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  React.useEffect(t1, t2);
  const [place, setPlace] = React.useState(placeName || "");
  let t3;
  let t4;
  if ($[4] !== placeName) {
    t3 = () => {
      if (placeName) {
        setPlace(placeName);
      }
    };
    t4 = [placeName];
    $[4] = placeName;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  React.useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== mapViewportManuallyChanged) {
    t5 = () => {
      if (mapViewportManuallyChanged) {
        setPlace("Map Search");
      }
    };
    t6 = [mapViewportManuallyChanged];
    $[7] = mapViewportManuallyChanged;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  React.useEffect(t5, t6);
  const [guestCountHumanReadableValue, setGuestCountHumanReadableValue] = React.useState("");
  let t7;
  let t8;
  if ($[10] !== adultCount || $[11] !== childCount || $[12] !== infantCount || $[13] !== petCount) {
    t7 = () => {
      const description = guestDescription(adultCount, childCount, infantCount, petCount);
      setGuestCountHumanReadableValue(description);
    };
    t8 = [adultCount, childCount, infantCount, petCount];
    $[10] = adultCount;
    $[11] = childCount;
    $[12] = infantCount;
    $[13] = petCount;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  React.useEffect(t7, t8);
  if (!hasInitialized) {
    let t9;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <DesktopSearchNavSkeleton />;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    return t9;
  }
  const t9 = !searchNavFormActive ? "border-b border-gray-light shadow-sm" : "";
  let t10;
  if ($[17] !== t9) {
    t10 = classNames("hidden lg:block sticky top-0 z-50 bg-white", t9);
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== place) {
    t11 = <NavSearchForm place={place} />;
    $[19] = place;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Link to={routes.home} className="w-1/2 lg:w-3/12"><img src="/assets/svg/logos/savvy_wordmark_slate.svg" className="block h-10 w-auto" alt="Savvy" /></Link>;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== activateFeaturesModal || $[23] !== activateSearchNavForm || $[24] !== checkInDate || $[25] !== checkOutDate || $[26] !== guestCountHumanReadableValue || $[27] !== place || $[28] !== searchNavFormActive) {
    t13 = !searchNavFormActive && <div className="flex flex-row w-full space-x-2 items-center justify-center"><button type="button" className="hover:shadow-md shadow-sm flex flex-row rounded-full h-[48px] py-1.5 px-[7px] text-primary-main font-medium text-md border border-gray-light whitespace-nowrap items-center" onClick={activateSearchNavForm}><div className="border-r-2 border-gray-light px-4 min-w-[8px]">{extractFirstPhraseBeforeComma(place || "Map Search")}</div><div className="border-r-2 border-gray-light px-4 min-w-[80px]" suppressHydrationWarning={true}>{checkInDate && checkOutDate ? dayjs.utc(checkInDate).month() === dayjs.utc(checkOutDate).month() ? `${dayjs.utc(checkInDate).format("MMM D")} - ${dayjs.utc(checkOutDate).format("D")}` : `${dayjs.utc(checkInDate).format("MMM D")} - ${dayjs.utc(checkOutDate).format("MMM D")}` : "Add dates"}</div><div className="pl-4 min-w-[60px] max-w-[155px] overflow-hidden text-ellipsis">{guestCountHumanReadableValue || "Add guests"}</div><div className="pl-4"><div className="flex items-center justify-center bg-primary-main rounded-full text-white p-2 h-[34px] w-[34px]"><Icon size="sm" name={IconNames.MAGNIFYING_GLASS} /></div></div></button><AnalyticsRef name="select_search_filters"><ButtonIcon icon={IconNames.FILTER} iconSize="sm" ariaLabel="Filters" variant="outlined" isCircle={true} isFullWidth={true} className="focus:outline-none h-9 w-9" data-testid="@filters/filters-btn" onClick={activateFeaturesModal} /></AnalyticsRef></div>;
    $[22] = activateFeaturesModal;
    $[23] = activateSearchNavForm;
    $[24] = checkInDate;
    $[25] = checkOutDate;
    $[26] = guestCountHumanReadableValue;
    $[27] = place;
    $[28] = searchNavFormActive;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== searchNavFormActive) {
    t14 = searchNavFormActive && <div className="flex items-center justify-center h-full"><Typography component="span" className="text-lg font-semibold text-primary-main">Find vacation rentals you love. Pay hundreds less.</Typography></div>;
    $[30] = searchNavFormActive;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t13 || $[33] !== t14) {
    t15 = <div className="hidden lg:flex justify-center w-6/12 h-[48px] relative">{t13}{t14}</div>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== session) {
    t16 = session?.user && session.user.isHost ? <Link to={routes.accountListings} className="ml-4 hidden text-sm md:inline-block">Manage your Properties</Link> : <Link to={routes.list} className="ml-4 hidden text-sm md:inline-block">List your Properties</Link>;
    $[35] = session;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <UserMenu />;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t16) {
    t18 = <div className="flex items-center justify-end w-1/2 lg:w-3/12">{t16}{t17}</div>;
    $[38] = t16;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t15 || $[41] !== t18) {
    t19 = <div className="py-4 px-4 sm:px-8"><div className="flex justify-between items-center">{t12}{t15}{t18}</div></div>;
    $[40] = t15;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t10 || $[44] !== t11 || $[45] !== t19) {
    t20 = <nav aria-label="Main navigation" className={t10}>{t11}{t19}</nav>;
    $[43] = t10;
    $[44] = t11;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  return t20;
};